export type HubAccordionSection = {
  icon: string;
  title: string;
  content?: string;
  items?: string[];
  expanded?: boolean;
};

export type HubHowItWorksStep = {
  title: string;
  text: string;
};

export type HubProgram = {
  slug: string;
  title: string;
  description: string;
  cardDescription: string;
  image: string;
  imageMobile?: string;
  icon: string;
  featured?: boolean;
  route?: string;
  ctaLabel?: string;
  ctaKind?: "avaliacao" | "participar" | "navigate";
  ctaHint?: string;
  consent?: {
    label: string;
    linkLabel: string;
  };
  intro?: string;
  highlights?: string[];
  highlightsNote?: string;
  highlightsIcon?: string;
  sections?: HubAccordionSection[];
  warning?: string;
  howItWorks?: HubHowItWorksStep[];
};

export type HubOption = {
  id: string;
  title: string;
  description: string;
  image: string;
  imageMobile?: string;
  icon: string;
  isNew?: boolean;
  route: string;
};

export type HubTeleconsulta = {
  id: string;
  title: string;
  description: string;
  image: string;
  imageMobile?: string;
  icon: string;
  ctaLabel: string;
  route: string;
  badge?: string;
};

const hubAsset = (file: string) => `${process.env.VUE_APP_BASE_URL || "/"}images/hub-saude/${file}`;
const hubIcon = (file: string) => `images/hub-saude/icons/${file}`;

export const HUB_SAUDE_COPY = {
  title: "Hub de saúde",
  subtitle: "Teleconsultas, acompanhamento de saúde e programas personalizados para você."
};

export const hubFeaturedCard = {
  eyebrow: "Indicado para você",
  badge: "COMECE POR AQUI",
  title: "Avaliação de saúde",
  description: "Realize seu check-up digital",
  ctaLabel: "Responder",
  image: hubAsset("avaliacao-saude.png"),
  route: "/hub-de-saude/programas/gerenciamento-de-caso"
};

export const hubOptions: HubOption[] = [
  {
    id: "dispositivos",
    title: "Conecte seus dispositivos",
    description:
      "Tenha seus dados de saúde reunidos de forma simples e integrada no seu dispositivo. Acompanhe seus passos, sono e muito mais.",
    image: hubAsset("conecte-dispositivos.png"),
    imageMobile: hubAsset("conecte-dispositivos-mobile.png"),
    icon: hubIcon("mobile-light-full.svg"),
    isNew: true,
    route: "/minha-saude"
  },
  {
    id: "scanner",
    title: "Análise de Pele",
    description:
      "Nossa ferramenta combina IA com conhecimento dermatológico para sugerir a melhor forma de cuidado para a sua pele. Fotografe pintas, manchas ou lesões e receba uma análise na hora.",
    image: hubAsset("scanner-dermatologico.png"),
    imageMobile: hubAsset("scanner-dermatologico-mobile.png"),
    icon: hubIcon("mobile-light-full.svg"),
    isNew: true,
    route: "/skiniver"
  },
  {
    id: "programas",
    title: "Programas de saúde",
    description:
      "Avaliação digital de saúde, equipe multidisciplinar e diferentes programas para cuidar de você em cada fase da vida.",
    image: hubAsset("programas-de-saude.png"),
    imageMobile: hubAsset("programas-de-saude-mobile.png"),
    icon: hubIcon("heart-light.svg"),
    route: "/hub-de-saude/programas"
  },
  {
    id: "teleconsultas",
    title: "Teleconsultas",
    description:
      "Agende teleconsultas para acompanhamento contínuo ou use o pronto atendimento digital para casos leves, como febre e gripe.",
    image: hubAsset("teleconsultas.png"),
    imageMobile: hubAsset("teleconsultas-mobile.png"),
    icon: hubIcon("layer-1.svg"),
    route: "/hub-de-saude/teleconsultas"
  }
];

export const hubPrograms: HubProgram[] = [
  {
    slug: "equipe-de-saude",
    title: "Equipe de Saúde",
    description:
      "Agende consultas com sua equipe de saúde dedicada, conectada sempre que você precisar, com planejamento de saúde, ações preventivas, acompanhamento proativo e cuidado integrado.",
    cardDescription:
      "Agende consultas com sua equipe de saúde dedicada, conectada sempre que você precisar, com planejamento de saúde, ações preventivas, acompanhamento proativo e cuidado integrado.",
    image: hubAsset("equipe-saude.png"),
    imageMobile: hubAsset("equipe-saude-mobile.png"),
    icon: hubIcon("heart-light.svg"),
    featured: true,
    route: "/agendamento/equipe-saude",
    ctaKind: "navigate"
  },
  {
    slug: "gerenciamento-de-caso",
    title: "Avaliação de saúde",
    description:
      "Uma avaliação preventiva que utiliza protocolos médicos baseados em evidências para analisar seu perfil de saúde de forma rápida e personalizada.\nO objetivo é facilitar a detecção precoce de condições de saúde e apoiar decisões de cuidado práticas.",
    cardDescription:
      "Prevenção 100% digital: responda a um rápido questionário e receba uma avaliação sobre sua saúde.",
    image: hubAsset("avaliacao-saude.png"),
    imageMobile: hubAsset("avaliacao-saude-mobile.png"),
    icon: hubIcon("heart-light.svg"),
    featured: true,
    ctaLabel: "Fazer minha avaliação agora",
    ctaKind: "avaliacao",
    ctaHint: "Para participar, clique no botão abaixo:",
    sections: [
      {
        icon: "clipboard-list-check",
        title: "Como a avaliação é feita",
        expanded: true,
        content:
          "Em cerca de 5 minutos, você responde a um questionário inteligente que se adapta às suas respostas, como em uma consulta médica.\nO sistema identifica fatores de risco, recomenda os exames adequados, apresenta resultados e orientações personalizadas."
      }
    ],
    howItWorks: [
      {
        title: "Responda ao questionário online em cerca de 5 minutos.",
        text: "Perguntas simples sobre sua saúde e hábitos de vida, como em um bate-papo."
      },
      {
        title: "Envie seus exames recentes",
        text: "Faça o upload dos seus exames ou permita que a plataforma acesse os resultados direto dos laboratórios."
      },
      {
        title: "Pedido inteligente",
        text: "O sistema analisa o que você já fez e solicita apenas o necessário, sem repetições."
      },
      {
        title: "Resultados explicados",
        text: "Você recebe tudo traduzido de forma simples e clara, com encaminhamento médico personalizado."
      },
      {
        title: "Cuidado contínuo",
        text: "Além de orientações e encaminhamentos personalizados, a equipe de saúde Omint acompanha você em casos que precisem de atenção."
      }
    ]
  },
  {
    slug: "omint-toda-vida",
    title: "Omint Toda Vida",
    description:
      "Um programa de saúde focado no seu cuidado e bem-estar: Rede multidisciplinar exclusiva e integrada, acompanhamento especializado, prevenção, consultas, exames e terapias alternativas – tudo para melhorar a sua qualidade de vida enquanto caminhamos ao seu lado!",
    cardDescription:
      "Cuidado integrado com rede exclusiva: consultas, exames, prevenção, terapias alternativas e acompanhamento especializado ao seu lado.",
    image: hubAsset("toda-vida.jpg"),
    imageMobile: hubAsset("toda-vida-mobile.png"),
    icon: hubIcon("heart-light.svg"),
    featured: true,
    ctaLabel: "Quero participar",
    ctaKind: "participar",
    ctaHint: "Para participar, clique no botão abaixo:",
    highlights: [
      "Equipe multidisciplinar especializada com: fisioterapeutas, nutricionistas, psicólogos, gerontólogos e médicos geriatras.",
      "Prevenção, habilitação, reabilitação e cuidados paliativos.",
      "Monitoramento periódico, com equipe de navegação de cuidado.",
      "Central de Saúde 24 horas para dúvidas e emergências e muito mais!"
    ],
    highlightsNote: "Diferenciais exclusivos*:",
    highlightsIcon: "clipboard-list-check",
    consent: {
      label: "Li, concordo e aceito o",
      linkLabel: "Termo de Consentimento do Programa Cuidar+ Omint."
    },
    howItWorks: [
      {
        title: "Solicite sua participação",
        text: "Envie o interesse pelo programa e nossa equipe entra em contato para entender o seu momento de cuidado."
      },
      {
        title: "Avaliação personalizada",
        text: "A equipe multidisciplinar analisa o seu perfil e monta um plano de cuidados sob medida."
      },
      {
        title: "Acompanhamento contínuo",
        text: "Você passa a ter monitoramento, prevenção e suporte em consultas, exames e terapias."
      }
    ]
  },
  {
    slug: "boa-hora",
    title: "Boa Hora",
    description:
      "Durante a gestação o cuidado com cada detalhe faz toda a diferença. Pensando nisso, a Omint criou o Programa Boa Hora, um acompanhamento completo que orienta mães e familiares desde a gravidez até o pós-parto, para que esse momento seja vivido com tranquilidade, conforto e segurança.",
    cardDescription:
      "Orientação personalizada para mães e familiares, da gestação ao pós-parto, com a atenção e cuidado que essa fase merece.",
    image: hubAsset("boa-hora.png"),
    imageMobile: hubAsset("boa-hora-mobile.png"),
    icon: hubIcon("heart-light.svg"),
    ctaLabel: "Quero participar",
    ctaKind: "participar",
    ctaHint: "Para participar, clique no botão abaixo:",
    sections: [
      {
        icon: "baby-carriage",
        title: "Alguns temas dos encontros",
        items: ["Parto", "Amamentação", "Alimentação", "Cuidados com o bebê"]
      }
    ],
    consent: {
      label: "Li, concordo e aceito o",
      linkLabel: "Termo de Consentimento do Programa Cuidar+ Omint."
    },
    howItWorks: [
      {
        title: "Participe do programa",
        text: "Solicite a inclusão e receba o convite para os encontros e o acompanhamento da equipe."
      },
      {
        title: "Encontros temáticos",
        text: "Orientações sobre parto, amamentação, alimentação e cuidados com o bebê."
      },
      {
        title: "Suporte até o pós-parto",
        text: "A equipe segue ao lado da família nessa fase, com atenção personalizada."
      }
    ]
  },
  {
    slug: "acompanhamento-de-caso",
    title: "Gerenciamento de Caso",
    description:
      "Auxiliamos os associados diagnosticados com patologias complexas na condução de sua evolução clínica, atuando na busca de medicamentos, administração da sua aplicação, suporte aos familiares que lidam diariamente com a nova condição do associado para preservar o bem-estar em suas relações.",
    cardDescription:
      "Acompanhamento completo para associados com patologias complexas: medicamentos, aplicação e suporte aos familiares.",
    image: hubAsset("gerenciamento-de-caso.png"),
    imageMobile: hubAsset("gerenciamento-de-caso-mobile.png"),
    icon: hubIcon("layer-1.svg"),
    ctaLabel: "Quero participar",
    ctaKind: "participar",
    warning:
      "Este programa não faz parte da cobertura do plano de saúde, e é oferecido pela Omint em caráter de exceção, sujeito à análise e aprovação prévia da equipe de auditoria médica.",
    howItWorks: [
      {
        title: "Avaliação clínica",
        text: "A equipe analisa o histórico e a complexidade do caso para indicar o melhor plano de acompanhamento."
      },
      {
        title: "Coordenação do cuidado",
        text: "Apoio na busca de medicamentos, aplicação e alinhamento com a equipe assistencial."
      },
      {
        title: "Suporte à família",
        text: "Orientação contínua para quem convive e cuida do associado no dia a dia."
      }
    ]
  },
  {
    slug: "concierge-de-medicamentos",
    title: "Concierge de medicamentos",
    description:
      "O Concierge de Medicamentos da Omint é um programa exclusivo voltado para o fornecimento, entrega e gerenciamento de tratamentos.",
    cardDescription:
      "O Concierge de Medicamentos da Omint é um programa exclusivo voltado para o fornecimento, entrega e gerenciamento de tratamentos.",
    image: hubAsset("concierge-medicamentos.png"),
    icon: hubIcon("mobile-light-full.svg"),
    ctaLabel: "Quero participar",
    ctaKind: "participar",
    howItWorks: [
      {
        title: "Solicite o programa",
        text: "Informe o interesse e a equipe avalia a elegibilidade e o protocolo do tratamento."
      },
      {
        title: "Gestão da medicação",
        text: "Acompanhamos fornecimento, entrega e orientação de uso conforme a prescrição."
      },
      {
        title: "Acompanhamento contínuo",
        text: "O programa segue gerenciando o tratamento para mais previsibilidade e segurança."
      }
    ]
  },
  {
    slug: "casa-segura",
    title: "Casa Segura",
    description:
      "Queremos estar ao seu lado em todos os momentos, fazendo cada vez mais parte da sua vida. Assim, criamos o Programa Casa Segura Omint, para ajudar os associados a partir de 65 anos a continuarem a aproveitar a vida em toda a sua plenitude.\n\nSegurança, acessibilidade, independência e autonomia são fundamentais para a realização das atividades cotidianas. Todos esses aspectos são avaliados pelo Casa Segura Omint.",
    cardDescription:
      "Para associados a partir de 65 anos, o Casa Segura Omint avalia o lar em segurança, acessibilidade e autonomia no dia a dia.",
    image: hubAsset("casa-segura.png"),
    icon: hubIcon("heart-light.svg"),
    ctaLabel: "Quero participar",
    ctaKind: "participar",
    warning:
      "Este programa não faz parte da cobertura do plano de saúde, e é oferecido pela Omint em caráter de exceção, sujeito à análise e aprovação prévia da equipe de auditoria médica."
  },
  {
    slug: "gerenciamento-de-longa-permanencia",
    title: "Gerenciamento de longa permanência",
    description:
      "Formamos equipes multidisciplinares para acompanhar à distância ou em domicílio associados com mais de 60 anos que se encontram em alguma condição crônica. Porque cuidar de perto é mais do que estar disponível. É estar presente sempre que você precisar.",
    cardDescription:
      "Equipes multidisciplinares acompanham associados 60+ com condições crônicas, à distância ou em domicílio, sempre que precisar.",
    image: hubAsset("gerenciamento-de-longa-permanencia.jpg"),
    icon: hubIcon("layer-1.svg"),
    ctaLabel: "Quero participar",
    ctaKind: "participar",
    ctaHint: "Para participar, clique no botão abaixo:",
    warning:
      "Este programa não faz parte da cobertura do plano de saúde, e é oferecido pela Omint em caráter de exceção, sujeito à análise e aprovação prévia da equipe de auditoria médica."
  }
];

export const hubTeleconsultas: HubTeleconsulta[] = [
  {
    id: "dr-omint",
    title: "Dr. Omint (Telemedicina)",
    description:
      "Agende uma consulta no mesmo dia com um médico credenciado Omint, para ter acompanhamento e encaminhamentos. Tudo 100% on-line, com qualidade, privacidade e segurança.",
    image: hubAsset("dr-omint.png"),
    imageMobile: hubAsset("dr-omint-mobile.png"),
    icon: hubIcon("heart-light.svg"),
    ctaLabel: "Agendar consulta",
    route: "/redireciona-dr-omint"
  },
  {
    id: "equipe-saude",
    title: "Equipe de Saúde",
    description:
      "Agende uma consulta com sua equipe de saúde dedicada, conectada sempre que você precisar, com planejamento de saúde, ações preventivas, acompanhamento proativo e cuidado integrado.",
    image: hubAsset("equipe-saude.png"),
    imageMobile: hubAsset("equipe-saude-mobile.png"),
    icon: hubIcon("heart-light.svg"),
    ctaLabel: "Agendar consulta",
    route: "/agendamento/equipe-saude"
  },
  {
    id: "pronto-atendimento",
    title: "Pronto Atendimento Digital - Einstein",
    description:
      "Indicado para situações agudas e de baixa complexidade. Consulte especialistas em clínica médica e pediatria com atendimento imediato, sem sair de casa e sem precisar de agendamento.",
    image: hubAsset("pronto-atendimento-einstein.png"),
    icon: hubIcon("mobile-light-full.svg"),
    ctaLabel: "Iniciar atendimento",
    route: "/redireciona-dr-omint",
    badge: "00h às 7h"
  }
];

export const featuredPrograms = hubPrograms.filter(program => program.featured);
export const extraPrograms = hubPrograms.filter(program => !program.featured);

const PROGRAM_SLUG_ALIASES: Record<string, string> = {
  "avaliacao-de-saude": "gerenciamento-de-caso"
};

export const getHubProgramBySlug = (slug?: string | null) => {
  if (!slug) return undefined;
  const resolvedSlug = PROGRAM_SLUG_ALIASES[slug] || slug;
  return hubPrograms.find(program => program.slug === resolvedSlug);
};
